import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, UploadCloudIcon, LinkIcon, Trash2Icon, RefreshCwIcon, SearchIcon, FileTextIcon, Loader2Icon, BookOpenIcon } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
import { useKnowledgeStore } from '../stores/knowledgeStore';
import { useUiStore } from '../stores/uiStore';
import { PageContainer, EmptyState, KnowledgeBaseStatusBadge, KnowledgeVisibilityBadge, DocumentStatusBadge } from '../components/common/page';
import { Card, Button, Badge, Input, Label } from '../components/common/ui';
import { Modal } from '../components/common/Modal';
import { formatRelative } from '../utils/format';
function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
export function KnowledgeBaseDetailPage() {
  useScreenInit();
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const kb = useKnowledgeStore(s => s.knowledgeBases.find(k => k.knowledgeBaseId === id));
  const documents = useKnowledgeStore(s => id ? s.documentsByKbId[id] || [] : []);
  const searchResults = useKnowledgeStore(s => id ? s.searchResults[id] : undefined);
  const searching = useKnowledgeStore(s => id ? s.searching[id] : false);
  const {
    deleteDocument,
    reindexKnowledgeBase,
    searchKnowledgeBase
  } = useKnowledgeStore();
  const pushToast = useUiStore(s => s.pushToast);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [urlOpen, setUrlOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  if (!kb || !id) {
    return <PageContainer data-id="element-644">
        <EmptyState icon={BookOpenIcon} title="知识库不存在" action={<Button onClick={() => navigate('/knowledge-bases')} data-id="element-646">
              返回知识库中心
            </Button>} data-id="element-645" />
      </PageContainer>;
  }
  const runSearch = () => {
    if (!searchQuery.trim()) return;
    searchKnowledgeBase(id, searchQuery.trim());
  };
  return <PageContainer data-id="element-647">
      <button onClick={() => navigate('/knowledge-bases')} className="mb-4 inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-200" data-id="element-648">
        <ArrowLeftIcon className="h-4 w-4" data-id="element-649" /> 返回知识库中心
      </button>

      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-650">
        <div data-id="element-651">
          <div className="flex items-center gap-2" data-id="element-652">
            <h2 className="text-xl font-semibold text-slate-100" data-id="element-653">{kb.name}</h2>
            <KnowledgeBaseStatusBadge status={kb.status} data-id="element-654" />
            <KnowledgeVisibilityBadge visibility={kb.visibility} data-id="element-655" />
          </div>
          <p className="mt-1 text-sm text-slate-400" data-id="element-656">{kb.description}</p>
          <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-500" data-id="element-657">
            <span data-id="element-658">{kb.documentCount} 文档</span>
            <span data-id="element-659">{kb.chunkCount} chunks</span>
            <span data-id="element-660">
              topK {kb.retrievalConfig.topK} · 阈值{' '}
              {kb.retrievalConfig.scoreThreshold}
            </span>
            <span data-id="element-661">被 {kb.usedByAgentCount} 个 Agent 引用</span>
          </div>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-662">
          <Button variant="outline" size="sm" onClick={() => setUploadOpen(true)} data-id="element-663">
            <UploadCloudIcon className="h-4 w-4" data-id="element-664" /> 上传文档
          </Button>
          <Button variant="outline" size="sm" onClick={() => setUrlOpen(true)} data-id="element-665">
            <LinkIcon className="h-4 w-4" data-id="element-666" /> URL 导入
          </Button>
          <Button variant="outline" size="sm" onClick={() => {
          reindexKnowledgeBase(id);
          pushToast('info', '已开始重建索引');
        }} data-id="element-667">
            <RefreshCwIcon className="h-4 w-4" data-id="element-668" /> 重建索引
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3" data-id="element-669">
        {/* Documents */}
        <div className="lg:col-span-2" data-id="element-670">
          <Card className="p-5" data-id="element-671">
            <h3 className="mb-4 text-sm font-semibold text-slate-200" data-id="element-672">
              文档管理
            </h3>
            {documents.length === 0 ? <EmptyState icon={FileTextIcon} title="暂无文档" description="上传文件或从 URL 导入文档" data-id="element-673" /> : <div className="space-y-2" data-id="element-674">
                {documents.map(d => <div key={d.documentId} className="flex items-center gap-3 rounded-lg border border-slate-800 px-3 py-2.5" data-id="element-675">
                    <div className="flex h-9 w-9 items-center justify-center rounded-md bg-slate-800" data-id="element-676">
                      {['parsing', 'chunking', 'embedding'].includes(d.status) ? <Loader2Icon className="h-4 w-4 animate-spin text-amber-400" data-id="element-677" /> : <FileTextIcon className="h-4 w-4 text-slate-400" data-id="element-678" />}
                    </div>
                    <div className="min-w-0 flex-1" data-id="element-679">
                      <div className="flex items-center gap-2" data-id="element-680">
                        <span className="truncate text-sm font-medium text-slate-200" data-id="element-681">
                          {d.filename}
                        </span>
                        <Badge tone={d.sourceType === 'url' ? 'amber' : 'blue'} data-id="element-682">
                          {d.sourceType === 'url' ? 'URL' : '上传'}
                        </Badge>
                      </div>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-500" data-id="element-683">
                        <span data-id="element-684">{formatBytes(d.sizeBytes)}</span>
                        <span data-id="element-685">{d.chunkCount} chunks</span>
                        <span data-id="element-686">{formatRelative(d.updatedAt)}</span>
                        {d.error && <span className="text-red-400" data-id="element-687">{d.error}</span>}
                      </div>
                    </div>
                    <DocumentStatusBadge status={d.status} data-id="element-688" />
                    <Button variant="ghost" size="sm" onClick={() => {
                deleteDocument(id, d.documentId);
                pushToast('success', '文档已删除');
              }} className="text-red-400 hover:text-red-300" aria-label="删除文档" data-id="element-689">
                      <Trash2Icon className="h-3.5 w-3.5" data-id="element-690" />
                    </Button>
                  </div>)}
              </div>}
          </Card>
        </div>

        {/* Retrieval test */}
        <div data-id="element-691">
          <Card className="p-5" data-id="element-692">
            <h3 className="mb-1 text-sm font-semibold text-slate-200" data-id="element-693">
              检索测试
            </h3>
            <p className="mb-3 text-xs text-slate-500" data-id="element-694">
              输入测试问题，查看召回片段与相似度
            </p>
            <div className="flex gap-2" data-id="element-695">
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onKeyDown={e => {
              if (e.key === 'Enter') runSearch();
            }} placeholder="输入测试问题" data-id="element-696" />
              <Button size="sm" onClick={runSearch} disabled={!searchQuery.trim() || searching} data-id="element-697">
                {searching ? <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-698" /> : <SearchIcon className="h-4 w-4" data-id="element-699" />}
              </Button>
            </div>
            <div className="mt-3 space-y-2" data-id="element-700">
              {searchResults?.map((hit, i) => <div key={hit.chunkId} className="rounded-lg border border-slate-800 bg-slate-950/40 p-2.5" data-id="element-701">
                  <div className="mb-1 flex items-center justify-between text-xs" data-id="element-702">
                    <span className="font-medium text-emerald-300" data-id="element-703">
                      [{i + 1}] {hit.documentName}
                    </span>
                    <span className="font-mono text-slate-500" data-id="element-704">
                      score {hit.score}
                    </span>
                  </div>
                  <p className="text-xs leading-relaxed text-slate-400" data-id="element-705">
                    {hit.snippet}
                  </p>
                </div>)}
              {searchResults && searchResults.length === 0 && <p className="text-sm text-slate-500" data-id="element-706">未召回相关片段。</p>}
            </div>
          </Card>
        </div>
      </div>

      <DocUploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} kbId={id} data-id="element-707" />
      <DocUrlModal open={urlOpen} onClose={() => setUrlOpen(false)} kbId={id} data-id="element-708" />
    </PageContainer>;
}
function DocUploadModal({
  open,
  onClose,
  kbId
}: {
  open: boolean;
  onClose: () => void;
  kbId: string;
}) {
  const uploadDocument = useKnowledgeStore(s => s.uploadDocument);
  const pushToast = useUiStore(s => s.pushToast);
  const [fileName, setFileName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  useEffect(() => {
    if (open) setFileName('');
  }, [open]);
  const submit = () => {
    if (!fileName) return;
    uploadDocument(kbId, fileName);
    pushToast('info', '文档已上传，正在解析与索引');
    onClose();
  };
  return <Modal open={open} onClose={onClose} title="上传文档" description="支持 PDF、Markdown、TXT、DOCX、CSV、HTML" footer={<>
          <Button variant="outline" onClick={onClose} data-id="element-710">
            取消
          </Button>
          <Button onClick={submit} disabled={!fileName} data-id="element-711">
            开始导入
          </Button>
        </>} data-id="element-709">
      <div onDragOver={e => {
      e.preventDefault();
      setDragOver(true);
    }} onDragLeave={() => setDragOver(false)} onDrop={e => {
      e.preventDefault();
      setDragOver(false);
      const f = e.dataTransfer.files[0];
      if (f) setFileName(f.name);
    }} className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-10 text-center transition-colors ${dragOver ? 'border-indigo-500 bg-indigo-500/5' : 'border-slate-700'}`} data-id="element-712">
        <UploadCloudIcon className="h-8 w-8 text-slate-500" data-id="element-713" />
        <p className="mt-2 text-sm text-slate-300" data-id="element-714">拖拽文档到此处，或</p>
        <label className="mt-2 cursor-pointer text-sm font-medium text-indigo-400 hover:text-indigo-300" data-id="element-715">
          选择文件
          <input type="file" className="hidden" accept=".pdf,.md,.txt,.docx,.csv,.html" onChange={e => setFileName(e.target.files?.[0]?.name || '')} data-id="element-716" />
        </label>
        {fileName && <p className="mt-3 rounded-md bg-slate-800 px-3 py-1 font-mono text-xs text-slate-300" data-id="element-717">
            {fileName}
          </p>}
      </div>
    </Modal>;
}
function DocUrlModal({
  open,
  onClose,
  kbId
}: {
  open: boolean;
  onClose: () => void;
  kbId: string;
}) {
  const importDocumentFromUrl = useKnowledgeStore(s => s.importDocumentFromUrl);
  const pushToast = useUiStore(s => s.pushToast);
  const [url, setUrl] = useState('');
  useEffect(() => {
    if (open) setUrl('');
  }, [open]);
  const submit = () => {
    if (!url.trim()) return;
    importDocumentFromUrl(kbId, url.trim());
    pushToast('info', '已开始抓取并导入 URL');
    onClose();
  };
  return <Modal open={open} onClose={onClose} title="URL 导入" description="输入网页或文档 URL，后端抓取并导入（限制内网地址，防 SSRF）" footer={<>
          <Button variant="outline" onClick={onClose} data-id="element-719">
            取消
          </Button>
          <Button onClick={submit} disabled={!url.trim()} data-id="element-720">
            导入
          </Button>
        </>} data-id="element-718">
      <div data-id="element-721">
        <Label required data-id="element-722">文档 URL</Label>
        <Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com/docs/guide.html" className="font-mono" data-id="element-723" />
      </div>
    </Modal>;
}